import { inject, Injectable } from '@angular/core';
import { IdentityService } from './identity.service';
import { map, Observable, Subject, Subscription } from 'rxjs';
import { Auth0Service } from './auth0.service';
import { jwtDecode } from 'jwt-decode';
import { AccessJwtPayload,
  AuthenticationTokenSet,
  IdentityJwtPayload,
  OAuthResponse } from '../models/authentication.model';
import { Router } from '@angular/router';
import { MpSnackbarService } from '../ui-components/mp-snackbar/mp-snackbar.service';
import { environment } from '../../environments/environment';
import { JsonUtils } from '../utils/json.utils';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  private static readonly TOKEN_SET_STORAGE_KEY: string = 'token_set';
  private static readonly REFRESHING_THRESHOLD_IN_MIN: number = 15;
  private authenticationTokenSet: AuthenticationTokenSet = {};

  private readonly identityService: IdentityService;
  private readonly auth0Service: Auth0Service;
  private readonly router: Router;
  private readonly snackbarService: MpSnackbarService;
  private readonly refreshTokenRequestsSubject = new Subject<AuthenticationTokenSet>;
  private refreshTokenSubscription: Subscription;
  private lastAttemptedUrl: string;

  constructor() {
    this.identityService = inject(IdentityService);
    this.auth0Service = inject(Auth0Service);
    this.router = inject(Router);
    this.snackbarService = inject(MpSnackbarService);
  }

  public setLastAttemptedUrl(url: string) {
    this.lastAttemptedUrl = url;
  }

  public isUserConnected(): boolean {
    return !!this.authenticationTokenSet?.accessToken &&
      !!this.authenticationTokenSet?.refreshToken;
  }

  public getAccessToken(): string {
    return this.authenticationTokenSet.accessToken;
  }

  public isUrlInAuthorizedUrls(url: string) {
    return environment.authorized_urls.some(authorizedUrl => url.startsWith(authorizedUrl));
  }

  public login$(email: string, password: string): Observable<string> {
    return this.auth0Service.getPasswordGrantCredentials(email, password)
      .pipe(
        map(response => {
          this.setAuthenticationTokenSet(response.access_token, response.refresh_token);
          this.setIdentity(response);
          return this.lastAttemptedUrl;
        })
      );
  }

  public logout() {
    this.destroyAuthenticationTokenSet();
    this.identityService.destroyIdentity();
    this.router.navigate(['authentication']);
  }

  public refreshAuthentication$(): Observable<AuthenticationTokenSet> {
    if (!this.refreshTokenSubscription) {

      if (this.authenticationTokenSet.refreshToken) {
        this.refreshAccessToken(this.authenticationTokenSet.refreshToken);
      } else {
        this.snackbarService.openSnackBarError('ERRORS.DEFAULT_TITLE', 'AUTHENTICATION.UNAUTHORIZED_USER');
        this.logout();
        // In this case we need to notify the subscriber that no refresh will be executed.
        // To do so we send an empty Identity object to prevent it to listen to the subject
        return new Observable<AuthenticationTokenSet>((observer) => {
          observer.next(undefined);
          observer.complete();
        });
      }
    }

    return this.refreshTokenRequestsSubject.asObservable();
  }


  public loadAuthenticationToken(): void {
    this.authenticationTokenSet = JsonUtils.parseJsonIfValid(localStorage.getItem(AuthenticationService.TOKEN_SET_STORAGE_KEY), {});
    this.identityService.loadIdentity();
  }

  public tokenNeedsRefreshing(): boolean {
    return this.authenticationTokenSet.expirationDateInMs < (Date.now() + (AuthenticationService.REFRESHING_THRESHOLD_IN_MIN * 60 * 1000));
  }

  private refreshAccessToken(refreshToken: string): void {
    this.refreshTokenSubscription = this.auth0Service.refreshToken(refreshToken)
      .subscribe({
        next: (response) => {
          this.setAuthenticationTokenSet(response.access_token, response.refresh_token);
          this.refreshIdentity(response);
          this.refreshTokenRequestsSubject.next(this.authenticationTokenSet);
          this.refreshTokenSubscription = undefined;
        },
        error: () => {
          this.snackbarService.openSnackBarError('ERRORS.DEFAULT_TITLE', 'AUTHENTICATION.UNAUTHORIZED_USER');
          this.logout();
          this.refreshTokenRequestsSubject.next(undefined);
          this.refreshTokenSubscription = undefined;
        }
      });
  }

  private setIdentity(response: OAuthResponse): void {
    const idPayload = jwtDecode<IdentityJwtPayload>(response.id_token);
    this.identityService.setIdentity(idPayload);
  }

  private refreshIdentity(response: OAuthResponse): void {
    const idPayload = jwtDecode<IdentityJwtPayload>(response.id_token);
    this.identityService.refreshIdentity(idPayload);
  }

  private setAuthenticationTokenSet(accessToken: string, refreshToken: string): void {
    const accessPayload = jwtDecode<AccessJwtPayload>(accessToken);
    this.authenticationTokenSet = new AuthenticationTokenSet();
    this.authenticationTokenSet.accessToken = accessToken;
    this.authenticationTokenSet.refreshToken = refreshToken;
    this.authenticationTokenSet.expirationDateInMs = accessPayload.exp * 1000;
    localStorage.setItem(AuthenticationService.TOKEN_SET_STORAGE_KEY, JSON.stringify(this.authenticationTokenSet));
  }

  private destroyAuthenticationTokenSet(): void {
    localStorage.removeItem(AuthenticationService.TOKEN_SET_STORAGE_KEY);
    this.authenticationTokenSet = {};
  }
}
